/**
 * Date utility functions for consistent timezone handling
 * All dates from backend are in UTC, these functions convert to local time (IST)
 */

/**
 * Format a UTC ISO string to local time string
 * @param {string} utcString - ISO date string in UTC
 * @param {object} options - Formatting options
 * @returns {string} Formatted local time string
 */
export const formatToLocalTime = (utcString, options = {}) => {
  if (!utcString) return '-';
  
  try {
    const date = new Date(utcString);
    
    // Default options for Indian locale
    const defaultOptions = {
      timeZone: 'Asia/Kolkata',
      ...options
    };
    
    return date.toLocaleString('en-IN', defaultOptions);
  } catch (error) {
    console.error('Error formatting date:', error);
    return utcString;
  }
};

/**
 * Format to local time only (HH:MM)
 * @param {string} utcString - ISO date string in UTC
 * @returns {string} Time in HH:MM format (local)
 */
export const formatToLocalTimeOnly = (utcString) => {
  if (!utcString) return '-';
  
  try {
    const date = new Date(utcString);
    return date.toLocaleTimeString('en-IN', {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting time:', error);
    return utcString?.slice(11, 16) || '-';
  }
};

/**
 * Format to local time with seconds (HH:MM:SS)
 * @param {string} utcString - ISO date string in UTC
 * @returns {string} Time in HH:MM:SS format (local)
 */
export const formatToLocalTimeWithSeconds = (utcString) => {
  if (!utcString) return '-';
  
  try {
    const date = new Date(utcString);
    return date.toLocaleTimeString('en-IN', {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting time:', error);
    return utcString?.slice(11, 19) || '-';
  }
};

/**
 * Format to local date and time
 * @param {string} utcString - ISO date string in UTC
 * @returns {string} Date and time in local format
 */
export const formatToLocalDateTime = (utcString) => {
  if (!utcString) return '-';
  
  try {
    const date = new Date(utcString);
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return utcString?.slice(0, 19).replace('T', ' ') || '-';
  }
};

/**
 * Format to local date only
 * @param {string} utcString - ISO date string in UTC
 * @returns {string} Date in local format
 */
export const formatToLocalDate = (utcString) => {
  if (!utcString) return '-';
  
  try {
    const date = new Date(utcString);
    return date.toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return utcString?.slice(0, 10) || '-';
  }
};
